import type { Example } from '../types';

export const animatedLightsExample: Example = {
  id: 'animated-lights',
  title: 'Animated Lights — LFOs & Orbits',
  categoryId: 'viewer',
  description: 'Light orbit paths and LFO intensity/color animation',
  scadCode: `/* Animated Lights — LFOs & Orbits */

// markdown:tip
// **Animated Lights** — Add \`orbit_radius\` + \`orbit_speed\` to any light
// to make it circle the model. Use \`lfo_intensity\` with a waveform string
// \`"shape:freq:phase:min:max"\` to pulse the brightness. \`lfo_color\`
// animates the light color between two RGB values.
// Hit **Pause** in the toolbar to freeze the animation.

/* [Orbiting Key Light] */

// Orbit radius
orbit_r = 100; // [30:5:200]

// Orbit speed (rev/s)
orbit_speed = 0.2; // [0.02:0.01:1.0]

// Orbit tilt (X angle)
orbit_tilt = 30; // [0:1:80]

// Key intensity
key_intensity = 1.8; // [0.2:0.1:4.0]

/* [Pulsing Fill Light] */

// Enable pulsing fill
pulse_fill = true;

// Pulse waveform
pulse_shape = "sine"; // [sine, triangle, square, sawtooth] {pulse_fill == true}

// Pulse frequency (Hz)
pulse_freq = 0.5; // [0.05:0.05:3.0] {pulse_fill == true}

// Intensity min
pulse_min = 0.1; // [0:0.05:1.5] {pulse_fill == true}

// Intensity max
pulse_max = 1.2; // [0:0.05:3.0] {pulse_fill == true}

/* [Color-Cycling Rim] */

// Enable color-cycling rim
color_rim = true;

// Cycle speed (Hz)
rim_freq = 0.15; // [0.02:0.01:1.0] {color_rim == true}

// --- parameters end ---

$fn = 64;

// Orbiting warm key light
light_directional(
  color=[1.0, 0.92, 0.8],
  position=[orbit_r, 0, 60],
  intensity=key_intensity,
  orbit_radius=orbit_r,
  orbit_tilt=[orbit_tilt, 0, 0],
  orbit_speed=orbit_speed
);

// Pulsing cool fill — lfo_intensity string: "shape:freq:phase:min:max"
lfo_intensity_str = str(pulse_shape, ":", pulse_freq, ":0:", pulse_min, ":", pulse_max);
if (pulse_fill)
  light_directional(
    color=[0.4, 0.6, 1.0],
    position=[-80, -60, 80],
    intensity=1.0,
    lfo_intensity=lfo_intensity_str
  );

// Color-cycling rim — lfo_color string: "shape:freq:phase:[r1,g1,b1]:[r2,g2,b2]"
lfo_color_str = str("sine:", rim_freq, ":0:[1,0.2,0.1]:[0.1,0.3,1]");
if (color_rim)
  light_directional(
    color=[1, 1, 1],
    position=[0, -100, 20],
    intensity=1.5,
    lfo_color=lfo_color_str
  );

// Ambient base
light_ambient(color=[1,1,1], intensity=0.25);

// Subject geometry — sphere on a pedestal
color([0.82, 0.80, 0.78])
material(material_def(metalness=0.8, roughness=0.08)) {
  sphere(r=20);
  translate([0, 0, -22]) cylinder(d=8, h=20, center=false);
  translate([0, 0, -26]) cylinder(d=30, h=4, center=false);
}

// Floor
color([0.12, 0.12, 0.14])
material(material_def(roughness=0.95))
translate([0, 0, -28])
  cube([100, 100, 2], center=true);
`,
};
